/**
 * Loại pin lithium khai báo khi tạo yêu cầu vận chuyển (発送依頼 / 返送依頼).
 *
 * Giá trị được gửi thẳng lên BE dưới dạng chuỗi JP và lưu nguyên vào DB
 * (`t_ship_to_buyer_requests.battery`, `t_storage_requests.battery`);
 * CMS bóc mã UN từ chuỗi này (`battery.match(/UN\d+/g)`) để bắt kho xác nhận dán nhãn,
 * nên KHÔNG được đổi phần `（UN....、PI...セクションII）` của các label bên dưới.
 *
 * Quy ước: khi user chọn `NO_BATTERY` thì FE gửi null/undefined lên BE (xem `isNoBattery`).
 */
export enum BatteryType {
  NO_BATTERY = 'バッテリなし',
  BUILT_IN_LITHIUM_METAL = '機器内に組み込まれているリチウムメタル電池（UN3091、PI970セクションII）',
  PACKAGED_LITHIUM_METAL = '機器と共に包装しているリチウムメタル電池（UN3091、PI969セクションII）',
  BUILT_IN_LITHIUM_ION = '機器内に組み込まれているリチウムイオン電池（UN3481、PI967セクションII）',
  PACKAGED_LITHIUM_ION = '機器と共に包装しているリチウムイオン電池（UN3481、PI966セクションII）',
}

export const BATTERY_TYPES: BatteryType[] = [
  BatteryType.NO_BATTERY,
  BatteryType.BUILT_IN_LITHIUM_METAL,
  BatteryType.PACKAGED_LITHIUM_METAL,
  BatteryType.BUILT_IN_LITHIUM_ION,
  BatteryType.PACKAGED_LITHIUM_ION,
];

/**
 * Màn 発送依頼 cũ từng dùng chuỗi `バッテリーなし` (có trường âm) nên phải nhận cả 2 biến thể,
 * tránh việc dữ liệu/state cũ bị hiểu nhầm là "có pin".
 */
export const isNoBattery = (battery?: string | null): boolean =>
  !battery || battery.includes('バッテリーなし') || battery.includes('バッテリなし');

/** Có pin lithium => cần gắn `extra.dangerous_goods` khi lấy rate USPS. */
export const hasLithiumBattery = (battery?: string | null): boolean => !isNoBattery(battery);

/** Giá trị gửi lên BE: null khi không có pin. */
export const toBatteryPayload = (battery?: string | null): string | null => (isNoBattery(battery) ? null : battery);

/**
 * Lãnh thổ Mỹ (country vẫn = 'US' nhưng ngoài lãnh thổ hải quan) — trùng danh sách
 * US_TERRITORY_STATES bên BE `shippoApiService.createShipment`.
 */
const US_TERRITORY_STATES = ['GU', 'PR', 'VI', 'MP', 'AS'];

/**
 * KH chốt 2026-08-31: chỉ đơn nội địa Mỹ THẬT (50 bang + DC) mới được khai "Contains lithium
 * batteries" trên Shippo; gửi ra ngoài Mỹ HOẶC tới lãnh thổ Mỹ (GU/PR/VI/MP/AS) thì không —
 * BE cũng gỡ cờ ở `shippoApiService.createShipment`, FE check để bảng giá khớp label thật.
 */
export const isLithiumDeclarableDestination = (country?: string | null, state?: string | null): boolean =>
  (country ?? '').toUpperCase() === 'US' && !US_TERRITORY_STATES.includes((state ?? '').toUpperCase());

/**
 * KH đổi ý 2026-08-31 (bổ sung cho rule gỡ cờ dangerous_goods ở trên): có pin lithium mà gửi
 * RA NGOÀI nước Mỹ hoặc tới lãnh thổ Mỹ (GU/PR/VI/MP/AS) thì XÓA HẲN USPS khỏi danh sách carrier —
 * kiện pin quốc tế chỉ được đi UPS/FedEx (kho dán UN label thủ công, xem cms-un-label-usps.md).
 */
export const shouldBlockUspsForBattery = (
  battery?: string | null,
  country?: string | null,
  state?: string | null,
): boolean => hasLithiumBattery(battery) && !isLithiumDeclarableDestination(country, state);

/** Lọc USPS khỏi bộ rate (dùng kèm `shouldBlockUspsForBattery`). */
export const excludeUspsRates = <T extends { provider?: string }>(rates: T[]): T[] =>
  rates.filter((rate) => rate?.provider !== 'USPS');

/** `extra.dangerous_goods` của Shippo — chỉ dùng cho rate USPS. */
export const DANGEROUS_GOODS_LITHIUM = {
  contains: true,
  lithium_batteries: {
    contains: true,
  },
};
